import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { UploadCSVModal } from '~/components/ui/UploadCSVModal';
import { isImportResult, type ImportResult } from '~/utils/importResult';
import { MAX_UPLOAD_SIZE_MB, uploadErrorKey } from '~/utils/uploadError';
import { useMakeRequest } from '~/hooks/useMakeRequest';
import { invalidatePriorityQueue } from '~/api';
import { exportCsv, generateCsvTemplate } from '~/utils';
import type { RootState } from '~/redux/store';

const BUYER_INVENTORY_UPLOAD_URL = '/api/buyer-inventory/import';

const csvHeaders = [
  'Product Code',
  'Length',
  'Location Code',
  'Stock',
  'Target Weeks Covered',
  'Weeks Covered Before Monitor',
  'Weeks Covered Before Urgent',
  'Usage Rate Per Week',
  'Unit',
];

const handleDownloadTemplate = () => {
  generateCsvTemplate(csvHeaders, 'buyer_inventory_template.csv');
};

type BuyerInventoryUploadCsvProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  // The with-data export reads the flat rows InventoryTable mirrors into Redux,
  // so it is only offered while the flat view is mounted.
  readonly withDataEnabled: boolean;
};

export function BuyerInventoryUploadCsv({
  isOpen,
  onClose,
  withDataEnabled,
}: Readonly<BuyerInventoryUploadCsvProps>) {
  const { t } = useTranslation('inventory');
  const queryClient = useQueryClient();
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const { makeRequest: uploadCSVRequest, loading } =
    useMakeRequest<ImportResult>();

  const buyerInventories = useSelector(
    (state: RootState) => state.inventory.inventories
  );

  const handleImportError = (error: AxiosError) => {
    const responseData = error.response?.data;
    if (isImportResult(responseData)) {
      setImportResult(responseData);
      return;
    }
    setImportResult(null);
    toast.error(
      t(uploadErrorKey(responseData) ?? 'inventory.upload_failed', {
        maxSizeMB: MAX_UPLOAD_SIZE_MB,
      })
    );
  };

  const handleImportSuccess = (data: ImportResult) => {
    setUploadFile(null);
    onClose();
    toast.success(t('inventory.rows_updated', { count: data.updatedRows }));
    queryClient.invalidateQueries({ queryKey: ['buyerInventories'] });
    invalidatePriorityQueue(queryClient);
    setImportResult(null);
  };

  const handleUpload = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    uploadCSVRequest(
      BUYER_INVENTORY_UPLOAD_URL,
      'POST',
      formData,
      handleImportSuccess,
      handleImportError
    );
  };

  const handleDownloadTemplateWithData = () => {
    const values = buyerInventories?.map((inv) => [
      inv.platformProductsLength.platformProduct.code ?? '',
      inv.platformProductsLength.lengthDisplayValue ?? '',
      inv.location.code ?? '',
      inv.quantity.value.toString(),
      inv.buyerInventoryConfiguration?.targetWeeksCovered.toString() ?? '',
      inv.buyerInventoryConfiguration?.weeksCoveredBeforeMonitor.toString() ??
        '',
      inv.buyerInventoryConfiguration?.weeksCoveredBeforeUrgent.toString() ??
        '',
      inv.buyerInventoryConfiguration?.usageRatePerWeek.value.toString() ?? '',
      inv.buyerInventoryConfiguration?.usageRatePerWeek.unit.toString() ?? '',
    ]);

    exportCsv(
      csvHeaders,
      values || [],
      'buyer_inventory_template_and_filtered_data.csv'
    );
  };

  return (
    <UploadCSVModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setImportResult(null);
      }}
      onUpload={handleUpload}
      file={uploadFile}
      onFileChange={setUploadFile}
      loading={loading}
      importResult={importResult}
      requiredFieldsText={t('inventory.upload_csv_hint')}
      handleDownloadTemplate={handleDownloadTemplate}
      handleDownloadTemplateWithData={
        withDataEnabled ? handleDownloadTemplateWithData : undefined
      }
    />
  );
}
